/**
 * Mapping from Praktika patient_medical_history vchitemid codes to Principle
 * BASIC_MEDICAL_HISTORY_FORM_SCHEMA form paths.
 *
 * Codes not listed here are routed to MedicalHistoryFormPath.ConditionsOther
 * (history) or MedicalHistoryFormPath.AllergiesOther (allergies) so no data
 * is silently dropped.
 */

/**
 * Praktika `vchitemid` codes from the patient_medical_history and
 * patient_medical_allergy tables that the migration maps explicitly.
 */
export enum PraktikaMedicalItemId {
  Medications = 'MEDICATIONS',
  BloodPressure = 'BLOODPRESSURE',
  BloodPressureHigh = 'BLOODPRESSURE_HIGH',
  Hepatitis = 'HEPATITIS',
  Diabetes = 'DIABETES',
}

/**
 * Form schema paths within BASIC_MEDICAL_HISTORY_FORM_SCHEMA referenced by the
 * mapping. Centralised so the destination entity never hard-codes path strings.
 */
export enum MedicalHistoryFormPath {
  Medications = 'medicalHistory.medications',
  ConditionsOther = 'medicalHistory.conditions.other',
  AllergiesOther = 'medicalHistory.allergies.other',
}

/**
 * Enum values accepted by the Principle medical-history form fields.
 * Mirrors the `enum` arrays declared in BASIC_MEDICAL_HISTORY_FORM_SCHEMA.
 */
export enum BloodPressureValue {
  High = 'High',
  Low = 'Low',
}

export enum HepatitisValue {
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
}

export enum DiabetesValue {
  TypeOne = 'Type I',
  TypeTwo = 'Type II',
}

/**
 * Conditions that map directly to a boolean form field.
 * Value: dot-notation path within the form schema.
 */
export const PRAKTIKA_MEDICAL_HISTORY_BOOLEAN_MAPPING: Record<string, string> =
  {
    ASTHMA: 'medicalHistory.conditions.asthma',
    EPILEPSY: 'medicalHistory.conditions.epilepsy',
    HIV: 'medicalHistory.conditions.hiv',
    RHEUMATICFEVER: 'medicalHistory.conditions.rheumaticFever',
    STROKE: 'medicalHistory.conditions.stroke',
    ARTHRITIS: 'medicalHistory.conditions.arthritis',
    OSTEOPOROSIS: 'medicalHistory.conditions.arthritis', // nearest match
    THYROID: 'medicalHistory.conditions.thyroidDisorder',
    HEART_VALVE: 'medicalHistory.conditions.heartCondition',
    HEART: 'medicalHistory.conditions.cardiacSurgery',
    HEART_ATTACK: 'medicalHistory.conditions.cardiacSurgery',
    ARRHYTHMIA: 'medicalHistory.conditions.cardiacSurgery',
    KIDNEY: 'medicalHistory.conditions.kidneyDisease',
    LIVER: 'medicalHistory.conditions.kidneyDisease', // "Kidney / Liver disease"
    HAEMOPHILIA: 'medicalHistory.conditions.bleeding',
    BLOODDISEASE: 'medicalHistory.conditions.bleeding',
    BLOODDISORDER: 'medicalHistory.conditions.bleeding',
    IMMUNITY: 'medicalHistory.conditions.immuneDeficiency',
    GASTRIC: 'medicalHistory.conditions.hasGastrointestinalDisorder',
    BOWEL: 'medicalHistory.conditions.hasGastrointestinalDisorder',
    PSYCHOLOGICAL: 'medicalHistory.conditions.hasPsychiatricDisorder',
    ANXIETY: 'medicalHistory.conditions.hasPsychiatricDisorder',
    DEPRESSION: 'medicalHistory.conditions.hasPsychiatricDisorder',
    CANCER: 'medicalHistory.conditions.hasCancer',
    PREGNANCY: 'medicalHistory.conditions.isPregnant',
    BREAST_FEEDING: 'medicalHistory.conditions.isPregnant', // nearest
    SMOKER: 'medicalHistory.conditions.isSmoker',
    JOINTREPLACEMENT: 'medicalHistory.conditions.hasJointReplacement',
    ABCOVER: 'dentalHistory.antibioticsBeforeTreatment',
  };

/**
 * Conditions where the vchvalue supplies the string value for an adjacent
 * free-text field (written in addition to the boolean sibling above).
 */
export const PRAKTIKA_MEDICAL_HISTORY_STRING_MAPPING: Record<string, string> = {
  JOINTREPLACEMENT: 'medicalHistory.conditions.replacedJoint',
  SMOKER: 'medicalHistory.conditions.amountSmoked',
  GASTRIC: 'medicalHistory.conditions.gastrointestinalDisorder',
  BOWEL: 'medicalHistory.conditions.gastrointestinalDisorder',
  PSYCHOLOGICAL: 'medicalHistory.conditions.psychiatricDisorderType',
  ANXIETY: 'medicalHistory.conditions.psychiatricDisorderType',
  DEPRESSION: 'medicalHistory.conditions.psychiatricDisorderType',
  CANCER: 'medicalHistory.conditions.cancerType',
  [PraktikaMedicalItemId.Medications]: MedicalHistoryFormPath.Medications,
};

/**
 * Codes that map to enum fields.
 * Key = itemId, value = form path.
 * The actual enum value is determined by parseMedicalHistoryEnumValue().
 */
export const PRAKTIKA_MEDICAL_HISTORY_ENUM_MAPPING: Record<string, string> = {
  [PraktikaMedicalItemId.BloodPressure]:
    'medicalHistory.conditions.bloodPressure',
  [PraktikaMedicalItemId.BloodPressureHigh]:
    'medicalHistory.conditions.bloodPressure',
  [PraktikaMedicalItemId.Hepatitis]: 'medicalHistory.conditions.hepatitis',
  [PraktikaMedicalItemId.Diabetes]: 'medicalHistory.conditions.diabetes',
};

function parseBloodPressureValue(
  rawValue: string | null
): BloodPressureValue | undefined {
  const normalised = (rawValue ?? '').toLowerCase();
  if (normalised.includes('low') || normalised.includes('hypotension')) {
    return BloodPressureValue.Low;
  }
  if (normalised.includes('high') || normalised.includes('hypertension')) {
    return BloodPressureValue.High;
  }
  // Ambiguous value (e.g. numeric reading or blank) — caller routes the raw
  // value to conditions.other rather than guessing a clinically meaningful
  // direction.
  return undefined;
}

function parseHepatitisValue(rawValue: string | null): HepatitisValue {
  const normalised = (rawValue ?? '').toUpperCase();
  const matchedType = Object.values(HepatitisValue).find((type) =>
    normalised.includes(type)
  );

  return matchedType ?? HepatitisValue.B;
}

function parseDiabetesValue(rawValue: string | null): DiabetesValue {
  const normalised = (rawValue ?? '').toLowerCase();
  const isTypeOne =
    normalised.includes('1') ||
    normalised.includes('i') ||
    normalised.includes('one');

  return isTypeOne ? DiabetesValue.TypeOne : DiabetesValue.TypeTwo;
}

/**
 * Parse the vchvalue from the DB into the enum value expected by the form.
 */
export function parseMedicalHistoryEnumValue(
  itemId: string,
  rawValue: string | null
): string | undefined {
  switch (itemId) {
    case PraktikaMedicalItemId.BloodPressureHigh:
      return BloodPressureValue.High;
    case PraktikaMedicalItemId.BloodPressure:
      return parseBloodPressureValue(rawValue);
    case PraktikaMedicalItemId.Hepatitis:
      return parseHepatitisValue(rawValue);
    case PraktikaMedicalItemId.Diabetes:
      return parseDiabetesValue(rawValue);
    default:
      return;
  }
}

/**
 * Allergy codes that map to boolean form fields.
 */
export const PRAKTIKA_ALLERGY_BOOLEAN_MAPPING: Record<string, string> = {
  PENICILLIN: 'medicalHistory.allergies.penicillin',
  CODEINE: 'medicalHistory.allergies.codeine',
  OPIATE: 'medicalHistory.allergies.codeine', // nearest
  IODINE: 'medicalHistory.allergies.iodine',
  LATEX: 'medicalHistory.allergies.latex',
  SALICYLATES: 'medicalHistory.allergies.aspirin',
};

/**
 * Human-readable labels for allergy codes routed to 'other'.
 * Codes not listed here use the raw itemId as the label.
 */
export const PRAKTIKA_ALLERGY_LABELS: Record<string, string> = {
  AMALGAM: 'Amalgam',
  ANAESTHESIA: 'Local anaesthesia',
  ANIMAL: 'Animal allergy',
  BARBITURATE: 'Barbiturate',
  BENZOCAINE: 'Benzocaine',
  CHROMIUM: 'Chromium',
  DRUG: 'Drug allergy',
  FOOD: 'Food allergy',
  HAYFEVER: 'Hayfever',
  HEPARIN: 'Heparin',
  INSULIN: 'Insulin',
  MEPROBAMATE: 'Meprobamate',
  NICKEL: 'Nickel',
  OTHER_A: 'Other allergy',
  PROCAINAMIDE: 'Procainamide',
  PROCAINE: 'Procaine',
  PROPOXYCAINE: 'Propoxycaine',
  STINGS: 'Insect stings',
  SULFONAMIDE: 'Sulfonamide',
  TETRACAINE: 'Tetracaine',
  THIAZIDE: 'Thiazide',
};

/**
 * Human-readable labels for history codes routed to conditions.other.
 */
export const PRAKTIKA_MEDICAL_HISTORY_OTHER_LABELS: Record<string, string> = {
  BLOODPRESSURE: 'Blood pressure',
  HOSPITALISED: 'Hospitalised',
  UNDERCARE: 'Under medical care',
  ALLERGIES: 'Allergies (see allergy section)',
  CONTRACEPTION: 'Contraception',
  ALCOHOL: 'Alcohol use',
  DRUGS: 'Drug use',
  BREATHING: 'Breathing disorder',
  HEADACHE: 'Headaches/migraines',
  ANAESTHETIC: 'Anaesthetic reaction',
  LEARNING: 'Learning disability',
  ATOPIC: 'Atopic condition',
  FLUID_EXCESS: 'Fluid retention',
  TUBERCULOSIS: 'Tuberculosis',
  SPECIAL_CONDITION: 'Special condition',
  OTHER: 'Other condition',
};
